/**
 * PDF 解析の経路判定（ハイブリッド・フォールバック）。
 *
 * <p>サーバのルール解析が低信頼のとき、対応ブラウザでは端末内 LLM（BROWSER_LLM）を優先し、
 * 利用不可・失敗時はサーバのフォールバック API（SERVER_LLM）へ切り替える。端末内経路では
 * 給与データをネットワークへ送信しない（憲章 IV / FR-003）。</p>
 */

import { SalarySlipData } from '../types/PayrollTypes';
import { apiEndpoints } from './apiConfig';
import {
  BrowserLlmAvailability,
  ParsedSlip,
  computeBrowserConfidence,
  extractWithBrowserLlm,
  getBrowserLlmAvailability,
} from './browserLlm';
import { validateSlip } from './payslipValidator';

/** PDF を解析し、経路に応じた `analysisMethod` を持つ結果を返す。 */
export async function analyzePdf(file: File): Promise<SalarySlipData> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${apiEndpoints.pdfAnalyze}?clientFallback=true`, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error('PDFの解析に失敗しました');
  }

  const result: SalarySlipData = await response.json();

  // 高信頼（ルール解析で完結）またはフォールバック用テキストが無い場合はそのまま返す
  if (!result.requiresClientFallback) {
    return result;
  }
  const fallbackText = result.fallbackText;
  if (!fallbackText) {
    return result;
  }

  // モデルが 'available' のときのみ端末内解析を使う（MI-6）
  const availability = await getBrowserLlmAvailability();
  if (availability === 'available') {
    try {
      const parsed = await extractWithBrowserLlm(fallbackText);
      const validation = validateSlip({
        ...parsed,
        confidence: computeBrowserConfidence(parsed),
      });
      return {
        ...result,
        ...definedFields(parsed),
        analysisMethod: 'BROWSER_LLM',
        confidence: validation.confidence,
        // サーバ由来の「クライアントでのAI解析が必要です」は引き継がず、検証警告のみを設定（MA-1）
        warnings: validation.warnings,
        requiresClientFallback: false,
        fallbackText: null,
      };
    } catch {
      // 端末内解析の失敗・スキーマ違反はサーバフォールバックへ切り替える（FR-009）
    }
  }

  return withAvailabilityNote(await analyzeTextOnServer(fallbackText), availability);
}

/** 定義済み（undefined でない）項目のみを抽出する（MI-3）。 */
function definedFields(parsed: ParsedSlip): Partial<SalarySlipData> {
  const fields: Partial<SalarySlipData> = {};
  if (parsed.salaryDate) {
    fields.salaryDate = parsed.salaryDate;
  }
  if (parsed.isBonus !== undefined) {
    fields.isBonus = parsed.isBonus;
  }
  if (parsed.grossSalary !== undefined) {
    fields.grossSalary = parsed.grossSalary;
  }
  if (parsed.healthInsurance !== undefined) {
    fields.healthInsurance = parsed.healthInsurance;
  }
  if (parsed.employmentInsurance !== undefined) {
    fields.employmentInsurance = parsed.employmentInsurance;
  }
  if (parsed.nationalPension !== undefined) {
    fields.nationalPension = parsed.nationalPension;
  }
  if (parsed.incomeTax !== undefined) {
    fields.incomeTax = parsed.incomeTax;
  }
  if (parsed.localTax !== undefined) {
    fields.localTax = parsed.localTax;
  }
  if (parsed.definedContribution !== undefined) {
    fields.definedContribution = parsed.definedContribution;
  }
  if (parsed.netSalary !== undefined) {
    fields.netSalary = parsed.netSalary;
  }
  return fields;
}

/** サーバフォールバック API で解析する（マスクはサーバ側で実施）。 */
async function analyzeTextOnServer(text: string): Promise<SalarySlipData> {
  const response = await fetch(apiEndpoints.pdfAnalyzeText, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!response.ok) {
    throw new Error('サーバでの解析に失敗しました');
  }
  return response.json();
}

/** モデル未ダウンロード・準備中でサーバへ切り替えた理由を警告として付す（MI-6）。 */
function withAvailabilityNote(data: SalarySlipData, availability: BrowserLlmAvailability): SalarySlipData {
  const note = availabilityNote(availability);
  if (!note) {
    return data;
  }
  return { ...data, warnings: [...(data.warnings ?? []), note] };
}

function availabilityNote(availability: BrowserLlmAvailability): string | null {
  if (availability === 'downloadable') {
    return 'ブラウザ内蔵AIモデルが未ダウンロードのため、サーバAIで解析しました';
  }
  if (availability === 'downloading') {
    return 'ブラウザ内蔵AIモデルの準備中のため、サーバAIで解析しました';
  }
  return null;
}
